import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Field, SaveBar, useDraft } from "@/components/hq/useDraft";
import { ImageField } from "@/components/hq/ImageField";
import { useData } from "@/context/DataContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

const inputCls = "rounded-sm bg-[#0A0A0A]";
const monoCls = "mono rounded-sm bg-[#0A0A0A] text-sm";

const CATEGORIES = [
  "BUILDING SMASH", "LEMONADING", "MARKET DAYS",
  "OPERATOR STORIES", "BUSINESS LESSONS", "BEHIND THE SCENES",
];

const Pick = ({ value, onChange, options, testId }) => (
  <select
    data-testid={testId}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    className="mono w-full rounded-sm border border-border bg-[#0A0A0A] px-3 py-2 text-xs text-white"
  >
    {options.map((o) => (
      <option key={String(o)} value={o}>{String(o)}</option>
    ))}
  </select>
);

const slugify = (s = "") =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

const ListEditor = ({ contentKey, testId, title, hint, blank, columns }) => {
  const { draft, update, save, dirty } = useDraft(contentKey);
  if (!draft) return null;
  const items = Array.isArray(draft) ? draft : [];
  const setItem = (id, patch) =>
    update(items.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  return (
    <div data-testid={testId}>
      <SaveBar
        dirty={dirty}
        save={save}
        testId={contentKey}
        addLabel="+ Add"
        onAdd={() => update([...items, { ...blank(), id: `${contentKey}-${Date.now()}`, order: items.length + 1 }])}
      />
      <h3 className="display text-2xl uppercase">{title}</h3>
      <p className="mono mt-1 text-[10px] leading-relaxed text-muted-foreground">{hint}</p>

      <div className="mt-5 space-y-4">
        {items.map((item) => (
          <div
            key={item.id}
            data-testid={`${testId}-item-${item.id}`}
            className="rounded-sm border border-border bg-[#141414] p-4"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="mono text-[10px] tracking-widest text-muted-foreground">
                {item.published ? (
                  <span className="text-primary">PUBLISHED</span>
                ) : (
                  "DRAFT — NOT VISIBLE"
                )}
              </span>
              <div className="flex items-center gap-3">
                <button
                  data-testid={`${testId}-publish-${item.id}`}
                  onClick={() => setItem(item.id, { published: !item.published })}
                  className="mono border border-border px-3 py-1.5 text-[10px] tracking-widest text-primary hover:border-primary"
                >
                  {item.published ? "UNPUBLISH" : "PUBLISH"}
                </button>
                <button
                  onClick={() => update(items.filter((x) => x.id !== item.id))}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              {columns(item, (patch) => setItem(item.id, patch))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const HeroEditor = () => {
  const { draft, update, save, dirty } = useDraft("hero");
  if (!draft) return null;
  const set = (k) => (e) => update({ ...draft, [k]: e.target.value });
  return (
    <div data-testid="hq-hero">
      <SaveBar dirty={dirty} save={save} testId="hero" />
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="HEADLINE LINE 1">
          <Input data-testid="hq-hero-top" value={draft.headline_top || ""} onChange={set("headline_top")} className={inputCls} />
        </Field>
        <Field label="HEADLINE LINE 2 (ACCENTED)">
          <Input data-testid="hq-hero-bottom" value={draft.headline_bottom || ""} onChange={set("headline_bottom")} className={inputCls} />
        </Field>
        <Field label="PRIMARY CTA TEXT">
          <Input value={draft.primary_cta || ""} onChange={set("primary_cta")} className={inputCls} />
        </Field>
        <Field label="PRIMARY CTA LINK">
          <Input value={draft.primary_href || ""} onChange={set("primary_href")} className={monoCls} />
        </Field>
        <Field label="SECONDARY CTA TEXT">
          <Input value={draft.secondary_cta || ""} onChange={set("secondary_cta")} className={inputCls} />
        </Field>
        <Field label="SECONDARY CTA LINK">
          <Input value={draft.secondary_href || ""} onChange={set("secondary_href")} className={monoCls} />
        </Field>
        <Field label="BACKGROUND CLIP (FILE PATH OR INSTAGRAM / TIKTOK / YOUTUBE URL)">
          <Input data-testid="hq-hero-clip" value={draft.clip || ""} onChange={set("clip")} className={monoCls} />
        </Field>
        <Field label="POSTER IMAGE">
          <ImageField label="" testId="hq-hero-poster" preview={false}
            value={draft.poster || ""} onChange={(url) => update({ ...draft, poster: url })} />
        </Field>
      </div>
      <div className="mt-4">
        <Field label="SUPPORTING COPY (BLANK LINE BETWEEN PARAGRAPHS)">
          <Textarea value={draft.body || ""} onChange={set("body")} rows={5} className={inputCls} />
        </Field>
      </div>
    </div>
  );
};

export const StoriesEditor = () => (
  <ListEditor
    contentKey="stories"
    testId="hq-stories"
    title="Lemonading stories"
    hint="PASTE AN INSTAGRAM REEL, TIKTOK OR YOUTUBE LINK — OR A /brand FILE PATH. DRAFTS STAY PRIVATE UNTIL YOU PUBLISH."
    blank={() => ({ slug: "", name: "", business: "", city: "", lemon: "", built: "", video_url: "", thumbnail: "", body: "", published: false })}
    columns={(item, set) => (
      <>
        <Field label="NAME">
          <Input data-testid={`hq-story-name-${item.id}`} value={item.name || ""}
            onChange={(e) => set({ name: e.target.value, slug: item.slug || slugify(e.target.value) })}
            className={inputCls} />
        </Field>
        <Field label="BUSINESS">
          <Input value={item.business || ""} onChange={(e) => set({ business: e.target.value })} className={inputCls} />
        </Field>
        <Field label="CITY">
          <Input data-testid={`hq-story-city-${item.id}`} value={item.city || ""}
            onChange={(e) => set({ city: e.target.value })} className={inputCls} />
        </Field>
        <Field label="VIDEO (INSTAGRAM / TIKTOK / YOUTUBE / FILE)">
          <Input data-testid={`hq-story-video-${item.id}`} value={item.video_url || ""}
            onChange={(e) => set({ video_url: e.target.value })}
            placeholder="https://www.instagram.com/reel/XXXX/" className={monoCls} />
        </Field>
        <Field label="THUMBNAIL IMAGE">
          <ImageField label="" testId={`hq-story-thumb-${item.id}`} preview={false}
            value={item.thumbnail || ""} onChange={(url) => set({ thumbnail: url })} />
        </Field>
        <Field label="URL SLUG">
          <Input value={item.slug || ""} onChange={(e) => set({ slug: slugify(e.target.value) })} className={monoCls} />
        </Field>
        <Field label="SORT ORDER">
          <Input type="number" value={item.order ?? 0} onChange={(e) => set({ order: Number(e.target.value) })} className={inputCls} />
        </Field>
        <div className="md:col-span-2 space-y-4">
          <Field label="THEIR LEMON">
            <Textarea value={item.lemon || ""} onChange={(e) => set({ lemon: e.target.value })} rows={2} className={inputCls} />
          </Field>
          <Field label="WHAT THEY BUILT FROM IT">
            <Textarea value={item.built || ""} onChange={(e) => set({ built: e.target.value })} rows={2} className={inputCls} />
          </Field>
          <Field label="FULL STORY (BLANK LINE BETWEEN PARAGRAPHS)">
            <Textarea value={item.body || ""} onChange={(e) => set({ body: e.target.value })} rows={5} className={inputCls} />
          </Field>
        </div>
      </>
    )}
  />
);

export const JourneyEditor = () => (
  <ListEditor
    contentKey="journeyClips"
    testId="hq-journey"
    title="The Smash Journey feed"
    hint="VERTICAL CLIPS. PASTE AN INSTAGRAM / TIKTOK / YOUTUBE LINK FROM @SMASH_LEMONADE AND HIT PUBLISH."
    blank={() => ({ slug: "", title: "", category: "BUILDING SMASH", video_url: "", thumbnail: "", published: false })}
    columns={(item, set) => (
      <>
        <div className="md:col-span-2">
          <Field label="TITLE">
            <Input data-testid={`hq-clip-title-${item.id}`} value={item.title || ""}
              onChange={(e) => set({ title: e.target.value, slug: item.slug || slugify(e.target.value) })}
              className={inputCls} />
          </Field>
        </div>
        <Field label="CATEGORY">
          <Pick testId={`hq-clip-category-${item.id}`} value={item.category}
            onChange={(v) => set({ category: v })} options={CATEGORIES} />
        </Field>
        <Field label="SORT ORDER">
          <Input type="number" value={item.order ?? 0} onChange={(e) => set({ order: Number(e.target.value) })} className={inputCls} />
        </Field>
        <Field label="VIDEO (INSTAGRAM / TIKTOK / YOUTUBE / FILE)">
          <Input data-testid={`hq-clip-video-${item.id}`} value={item.video_url || ""}
            onChange={(e) => set({ video_url: e.target.value })}
            placeholder="https://www.instagram.com/reel/XXXX/" className={monoCls} />
        </Field>
        <Field label="THUMBNAIL IMAGE">
          <ImageField label="" testId={`hq-clip-thumb-${item.id}`} preview={false}
            value={item.thumbnail || ""} onChange={(url) => set({ thumbnail: url })} />
        </Field>
      </>
    )}
  />
);

export const ReelsEditor = () => (
  <ListEditor
    contentKey="reels"
    testId="hq-reels"
    title="Instagram reels"
    hint="PASTE 5–10 REEL URLS FROM @SMASH_LEMONADE AND HIT PUBLISH. ADD A THUMBNAIL IF YOU WANT A CUSTOM COVER — IT IS ALSO THE FALLBACK IF INSTAGRAM BLOCKS THE EMBED."
    blank={() => ({ url: "", caption: "", thumbnail: "", published: false })}
    columns={(item, set) => (
      <>
        <div className="md:col-span-2">
          <Field label="REEL URL">
            <Input data-testid={`hq-reel-url-${item.id}`} value={item.url || ""}
              onChange={(e) => set({ url: e.target.value.trim() })}
              placeholder="https://www.instagram.com/reel/XXXXXXXX/" className={monoCls} />
          </Field>
        </div>
        <Field label="CAPTION (OPTIONAL)">
          <Input value={item.caption || ""} onChange={(e) => set({ caption: e.target.value })} className={inputCls} />
        </Field>
        <Field label="THUMBNAIL / FALLBACK IMAGE">
          <ImageField label="" testId={`hq-reel-thumb-${item.id}`} preview={false}
            value={item.thumbnail || ""} onChange={(url) => set({ thumbnail: url })} />
        </Field>
        <Field label="SORT ORDER">
          <Input type="number" value={item.order ?? 0} onChange={(e) => set({ order: Number(e.target.value) })} className={inputCls} />
        </Field>
      </>
    )}
  />
);

export const LicenseEditor = () => {
  const { draft, update, save, dirty } = useDraft("license");
  if (!draft) return null;
  const setPillar = (id, patch) =>
    update({ ...draft, pillars: draft.pillars.map((p) => (p.id === id ? { ...p, ...patch } : p)) });

  return (
    <div data-testid="hq-license">
      <SaveBar
        dirty={dirty}
        save={save}
        testId="license"
        addLabel="+ Pillar"
        onAdd={() =>
          update({
            ...draft,
            pillars: [...(draft.pillars || []), { id: `lic-${Date.now()}`, title: "", body: "" }],
          })
        }
      />
      <h3 className="display text-2xl uppercase">One-year operator license</h3>
      <p className="mono mt-1 text-[10px] leading-relaxed text-muted-foreground">
        SHOWS ON /OPERATORS, /ACADEMY AND /APPLY. KEEP THE LEGAL NOTE IN PLACE — IT KEEPS THE PAGE
        A DESCRIPTION OF THE OFFER RATHER THAN AN OFFER TO SELL.
      </p>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <Field label="EYEBROW">
          <Input value={draft.eyebrow || ""}
            onChange={(e) => update({ ...draft, eyebrow: e.target.value })} className={inputCls} />
        </Field>
        <Field label="TITLE">
          <Input data-testid="hq-license-title" value={draft.title || ""}
            onChange={(e) => update({ ...draft, title: e.target.value })} className={inputCls} />
        </Field>
        <div className="md:col-span-2">
          <Field label="INTRO">
            <Textarea rows={2} value={draft.intro || ""}
              onChange={(e) => update({ ...draft, intro: e.target.value })} className={inputCls} />
          </Field>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {(draft.pillars || []).map((p) => (
          <div key={p.id} data-testid={`hq-license-pillar-${p.id}`} className="rounded-sm border border-border bg-[#141414] p-4">
            <div className="grid gap-3 md:grid-cols-[1fr_2fr]">
              <Field label="PILLAR TITLE">
                <Input data-testid={`hq-license-pillar-title-${p.id}`} value={p.title || ""}
                  onChange={(e) => setPillar(p.id, { title: e.target.value })} className={inputCls} />
              </Field>
              <Field label="WHAT IT COVERS">
                <Textarea rows={2} value={p.body || ""}
                  onChange={(e) => setPillar(p.id, { body: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <button
              onClick={() => update({ ...draft, pillars: draft.pillars.filter((x) => x.id !== p.id) })}
              className="mono mt-3 text-[10px] tracking-widest text-muted-foreground hover:text-destructive"
            >
              DELETE PILLAR
            </button>
          </div>
        ))}
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <Field label="RENEWAL HEADING">
          <Input value={draft.renewal_title || ""}
            onChange={(e) => update({ ...draft, renewal_title: e.target.value })} className={inputCls} />
        </Field>
        <Field label="RENEWAL TERMS">
          <Textarea data-testid="hq-license-renewal" rows={2} value={draft.renewal_body || ""}
            onChange={(e) => update({ ...draft, renewal_body: e.target.value })} className={inputCls} />
        </Field>
        <div className="md:col-span-2">
          <Field label="LEGAL NOTE (KEEP THIS)">
            <Textarea data-testid="hq-license-legal" rows={4} value={draft.legal_note || ""}
              onChange={(e) => update({ ...draft, legal_note: e.target.value })} className={inputCls} />
          </Field>
        </div>
      </div>
    </div>
  );
};

export const PackagesEditor = () => {
  const { draft, update, save, dirty } = useDraft("packages");
  if (!draft) return null;
  const setPack = (id, patch) =>
    update({ ...draft, packages: draft.packages.map((p) => (p.id === id ? { ...p, ...patch } : p)) });
  const setItem = (pack, itemId, patch) =>
    setPack(pack.id, {
      items: pack.items.map((it) => (it.id === itemId ? { ...it, ...patch } : it)),
    });

  return (
    <div data-testid="hq-packages">
      <SaveBar
        dirty={dirty}
        save={save}
        testId="packages"
        addLabel="+ Package"
        onAdd={() =>
          update({
            ...draft,
            packages: [...(draft.packages || []),
              { id: `pk-${Date.now()}`, name: "", price_label: "", summary: "", items: [] }],
          })
        }
      />
      <h3 className="display text-2xl uppercase">Operator packages</h3>
      <p className="mono mt-1 text-[10px] leading-relaxed text-muted-foreground">
        EXACTLY WHAT A TENT OR TRAILER OPERATOR RECEIVES. THIS RENDERS ON THE APPLY PAGE.
      </p>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <Field label="SECTION TITLE">
          <Input value={draft.title || ""} onChange={(e) => update({ ...draft, title: e.target.value })} className={inputCls} />
        </Field>
        <Field label="INTRO">
          <Input value={draft.intro || ""} onChange={(e) => update({ ...draft, intro: e.target.value })} className={inputCls} />
        </Field>
        <div className="md:col-span-2">
          <Field label="FOOTNOTE / DISCLAIMER">
            <Input data-testid="hq-packages-note" value={draft.note || ""}
              onChange={(e) => update({ ...draft, note: e.target.value })} className={inputCls} />
          </Field>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {(draft.packages || []).map((p) => (
          <div key={p.id} data-testid={`hq-package-${p.id}`} className="rounded-sm border border-border bg-[#141414] p-4">
            <div className="grid gap-3 md:grid-cols-3">
              <Field label="PACKAGE NAME">
                <Input data-testid={`hq-package-name-${p.id}`} value={p.name || ""}
                  onChange={(e) => setPack(p.id, { name: e.target.value })} className={`${inputCls} display text-lg`} />
              </Field>
              <Field label="PRICE LABEL">
                <Input value={p.price_label || ""} onChange={(e) => setPack(p.id, { price_label: e.target.value })} className={inputCls} />
              </Field>
              <Field label="ONE-LINE SUMMARY">
                <Input value={p.summary || ""} onChange={(e) => setPack(p.id, { summary: e.target.value })} className={inputCls} />
              </Field>
            </div>

            <div className="mono mt-5 text-[10px] tracking-widest text-muted-foreground">
              LINE ITEMS
            </div>
            <div className="mt-2 space-y-2">
              {(p.items || []).map((it) => (
                <div key={it.id} data-testid={`hq-package-item-${it.id}`} className="flex items-start gap-3">
                  <div className="w-20">
                    <Input data-testid={`hq-package-qty-${it.id}`} value={it.qty || ""}
                      onChange={(e) => setItem(p, it.id, { qty: e.target.value })}
                      placeholder="QTY" className={monoCls} />
                  </div>
                  <div className="flex-1">
                    <Input data-testid={`hq-package-label-${it.id}`} value={it.label || ""}
                      onChange={(e) => setItem(p, it.id, { label: e.target.value })}
                      placeholder="What they receive" className={inputCls} />
                  </div>
                  <div className="flex-1">
                    <Input value={it.note || ""} onChange={(e) => setItem(p, it.id, { note: e.target.value })}
                      placeholder="Note (optional)" className={inputCls} />
                  </div>
                  <button
                    onClick={() => setPack(p.id, { items: p.items.filter((x) => x.id !== it.id) })}
                    className="mt-2 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-4 flex items-center gap-4">
              <button
                data-testid={`hq-package-add-item-${p.id}`}
                onClick={() =>
                  setPack(p.id, {
                    items: [...(p.items || []), { id: `it-${Date.now()}`, label: "", qty: "1", note: "" }],
                  })
                }
                className="mono border border-border px-3 py-1.5 text-[10px] tracking-widest text-primary hover:border-primary"
              >
                + LINE ITEM
              </button>
              <button
                onClick={() => update({ ...draft, packages: draft.packages.filter((x) => x.id !== p.id) })}
                className="mono text-[10px] tracking-widest text-muted-foreground hover:text-destructive"
              >
                DELETE PACKAGE
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const LessonsEditor = () => (
  <ListEditor
    contentKey="lessons"
    testId="hq-lessons"
    title="Free lessons"
    hint="THESE ARE FREE AND PUBLIC. EACH ONE GETS ITS OWN SHAREABLE PAGE AT /learn/SLUG."
    blank={() => ({ slug: "", title: "", blurb: "", body: "", video_url: "", published: false })}
    columns={(item, set) => (
      <>
        <Field label="TITLE">
          <Input data-testid={`hq-lesson-title-${item.id}`} value={item.title || ""}
            onChange={(e) => set({ title: e.target.value, slug: item.slug || slugify(e.target.value) })}
            className={inputCls} />
        </Field>
        <Field label="URL SLUG">
          <Input value={item.slug || ""} onChange={(e) => set({ slug: slugify(e.target.value) })} className={monoCls} />
        </Field>
        <Field label="ONE-LINE BLURB">
          <Input value={item.blurb || ""} onChange={(e) => set({ blurb: e.target.value })} className={inputCls} />
        </Field>
        <Field label="VIDEO (OPTIONAL)">
          <Input value={item.video_url || ""} onChange={(e) => set({ video_url: e.target.value })} className={monoCls} />
        </Field>
        <Field label="SORT ORDER">
          <Input type="number" value={item.order ?? 0} onChange={(e) => set({ order: Number(e.target.value) })} className={inputCls} />
        </Field>
        <div className="md:col-span-2">
          <Field label="BODY — BLANK LINE BETWEEN PARAGRAPHS, **BOLD LEAD-IN** AT THE START OF A PARAGRAPH">
            <Textarea data-testid={`hq-lesson-body-${item.id}`} value={item.body || ""}
              onChange={(e) => set({ body: e.target.value })} rows={10} className={inputCls} />
          </Field>
        </div>
      </>
    )}
  />
);

export const StorylineEditor = () => {
  const { draft, update, save, dirty } = useDraft("timeline");
  if (!draft) return null;
  const setMilestone = (id, patch) =>
    update({ ...draft, milestones: draft.milestones.map((m) => (m.id === id ? { ...m, ...patch } : m)) });

  return (
    <div data-testid="hq-timeline">
      <SaveBar
        dirty={dirty}
        save={save}
        testId="timeline"
        addLabel="+ Milestone"
        onAdd={() =>
          update({ ...draft, milestones: [...draft.milestones, { id: `tl-${Date.now()}`, label: "", body: "", media: "" }] })
        }
      />
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="SECTION TITLE">
          <Input value={draft.title || ""} onChange={(e) => update({ ...draft, title: e.target.value })} className={inputCls} />
        </Field>
        <Field label="INTRO">
          <Input value={draft.intro || ""} onChange={(e) => update({ ...draft, intro: e.target.value })} className={inputCls} />
        </Field>
      </div>
      <div className="mt-5 space-y-3">
        {(draft.milestones || []).map((m) => (
          <div key={m.id} data-testid={`hq-timeline-${m.id}`} className="rounded-sm border border-border bg-[#141414] p-4">
            <div className="flex items-start gap-3">
              <div className="flex-1 grid gap-3 md:grid-cols-2">
                <Field label="LABEL">
                  <Input value={m.label || ""} onChange={(e) => setMilestone(m.id, { label: e.target.value })} className={`${inputCls} display text-lg`} />
                </Field>
                <Field label="PHOTO OR VIDEO (OPTIONAL)">
                  <Input value={m.media || ""} onChange={(e) => setMilestone(m.id, { media: e.target.value })} className={monoCls} />
                </Field>
                <div className="md:col-span-2">
                  <Field label="ONE LINE, DOCUMENTARY STYLE">
                    <Input value={m.body || ""} onChange={(e) => setMilestone(m.id, { body: e.target.value })} className={inputCls} />
                  </Field>
                </div>
              </div>
              <button
                onClick={() => update({ ...draft, milestones: draft.milestones.filter((x) => x.id !== m.id) })}
                className="mt-6 text-muted-foreground hover:text-destructive"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const FaqEditor = () => (
  <ListEditor
    contentKey="faq"
    testId="hq-faq"
    title="FAQ"
    hint="STRAIGHT ANSWERS. NEVER ADD EARNINGS CLAIMS HERE."
    blank={() => ({ q: "", a: "", published: true })}
    columns={(item, set) => (
      <>
        <div className="md:col-span-2">
          <Field label="QUESTION">
            <Input data-testid={`hq-faq-q-${item.id}`} value={item.q || ""} onChange={(e) => set({ q: e.target.value })} className={inputCls} />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="ANSWER">
            <Textarea value={item.a || ""} onChange={(e) => set({ a: e.target.value })} rows={4} className={inputCls} />
          </Field>
        </div>
      </>
    )}
  />
);

const BOOKING_STATUSES = ["NEW", "CONTACTED", "DEPOSIT SENT", "DEPOSIT PAID", "BALANCE SENT",
  "PAID IN FULL", "BOOKED", "DECLINED"];

/** Quote total from the HQ pricing, so the deposit pre-fills itself. */
const quoteTotal = (booking, cfg) => {
  if (!cfg) return 0;
  const drinks = parseInt(booking.drink_count, 10);
  if (!drinks) return 0;
  const pack = (cfg.packages || [])
    .filter((p) => p.price_amount > 0 && drinks <= p.drink_count)
    .sort((a, b) => a.drink_count - b.drink_count)[0];
  if (!pack) return 0;
  const service = (cfg.services || []).find((s) =>
    (booking.service_option || "").startsWith(s.name)
  );
  const alaniRate = (cfg.upgrades || []).find((u) => u.amount > 0)?.amount || 0;
  const alani = Math.min(parseInt(booking.alani_count, 10) || 0, drinks);
  return pack.price_amount + (service?.fee || 0) + alani * alaniRate;
};

const BalancePanel = ({ booking, total, onPaid }) => {
  const due = Math.max(total - Number(booking.deposit_amount || 0), 0);
  const [amount, setAmount] = useState(
    booking.balance_amount ? String(booking.balance_amount) : due ? due.toFixed(2) : ""
  );
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/balance`, {
        amount: Number(amount),
        origin_url: window.location.origin,
      });
      toast.success(data.emailed ? "Balance link emailed to the customer" : "Link created — email failed, copy it below");
      onPaid({ status: "BALANCE SENT", balance_amount: Number(amount), balance_url: data.checkout_url });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (booking.balance_paid)
    return (
      <div data-testid={`hq-balance-paid-${booking.id}`} className="mono mt-3 border-l-2 border-primary pl-4 text-[10px] leading-relaxed tracking-widest text-primary">
        PAID IN FULL — BALANCE OF ${Number(booking.balance_amount || 0).toLocaleString("en-US")} RECEIVED
        {booking.balance_paid_at ? ` ON ${booking.balance_paid_at.slice(0, 10)}` : ""}
      </div>
    );

  return (
    <div data-testid={`hq-balance-${booking.id}`} className="mt-4 border-t border-border/60 pt-4">
      <div className="mono text-[10px] leading-relaxed tracking-widest text-muted-foreground">
        FINAL BALANCE GOES OUT AUTOMATICALLY 7 DAYS BEFORE THE EVENT. SEND IT EARLY HERE IF YOU WANT.
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Field label="REMAINING BALANCE">
            <Input data-testid={`hq-balance-amount-${booking.id}`} value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="375" className={monoCls} />
          </Field>
        </div>
        <button
          data-testid={`hq-balance-send-${booking.id}`}
          disabled={busy || !Number(amount)}
          onClick={send}
          className="mono rounded-sm border border-primary px-4 py-2.5 text-[10px] tracking-widest text-primary transition-colors hover:bg-primary hover:text-primary-foreground disabled:opacity-40"
        >
          {busy ? "SENDING…" : booking.balance_url ? "RESEND BALANCE LINK" : "SEND BALANCE LINK"}
        </button>
        {booking.balance_url ? (
          <button
            data-testid={`hq-balance-copy-${booking.id}`}
            onClick={() => {
              navigator.clipboard.writeText(booking.balance_url);
              toast.success("Balance link copied");
            }}
            className="mono text-[10px] tracking-widest text-muted-foreground hover:text-primary"
          >
            COPY BALANCE LINK
          </button>
        ) : null}
      </div>
    </div>
  );
};

const DepositPanel = ({ booking, packages, onPaid }) => {
  const total = quoteTotal(booking, packages);
  const pct = packages?.deposit_pct || 50;
  const [amount, setAmount] = useState(
    booking.deposit_amount ? String(booking.deposit_amount) : total ? ((total * pct) / 100).toFixed(2) : ""
  );
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/deposit`, {
        amount: Number(amount),
        quote_total: total,
        origin_url: window.location.origin,
      });
      toast.success(data.emailed ? "Deposit link emailed to the customer" : "Link created — email failed, copy it below");
      onPaid({ status: "DEPOSIT SENT", deposit_amount: Number(amount), deposit_url: data.checkout_url });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (booking.deposit_paid)
    return (
      <>
        <div data-testid={`hq-deposit-paid-${booking.id}`} className="mono mt-4 border-l-2 border-primary pl-4 text-[10px] leading-relaxed tracking-widest text-primary">
          DEPOSIT OF ${Number(booking.deposit_amount || 0).toLocaleString("en-US")} PAID
          {booking.deposit_paid_at ? ` ON ${booking.deposit_paid_at.slice(0, 10)}` : ""}
        </div>
        <BalancePanel booking={booking} total={total} onPaid={onPaid} />
      </>
    );

  return (
    <div data-testid={`hq-deposit-${booking.id}`} className="mt-4 border-t border-border/60 pt-4">
      <div className="mono text-[10px] tracking-widest text-muted-foreground">
        QUOTED TOTAL {total ? `$${total.toLocaleString("en-US")}` : "— ADD DRINKS AND A SERVICE OPTION TO AUTO-CALCULATE"}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Field label={`DEPOSIT (${pct}%)`}>
            <Input data-testid={`hq-deposit-amount-${booking.id}`} value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="375" className={monoCls} />
          </Field>
        </div>
        <button
          data-testid={`hq-deposit-send-${booking.id}`}
          disabled={busy || !Number(amount)}
          onClick={send}
          className="mono rounded-sm border border-primary px-4 py-2.5 text-[10px] tracking-widest text-primary transition-colors hover:bg-primary hover:text-primary-foreground disabled:opacity-40"
        >
          {busy ? "SENDING…" : booking.deposit_url ? "RESEND DEPOSIT REQUEST" : "APPROVE & REQUEST DEPOSIT"}
        </button>
        {booking.deposit_url ? (
          <button
            data-testid={`hq-deposit-copy-${booking.id}`}
            onClick={() => {
              navigator.clipboard.writeText(booking.deposit_url);
              toast.success("Pay link copied");
            }}
            className="mono text-[10px] tracking-widest text-muted-foreground hover:text-primary"
          >
            COPY PAY LINK
          </button>
        ) : null}
      </div>
    </div>
  );
};

export const BookingsInbox = () => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const { content } = useData();
  const { draft, update, save, dirty } = useDraft("catering");

  useEffect(() => {
    api
      .get("/bookings")
      .then(({ data }) => setRows(data.bookings))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message));
  }, []);

  const setStatus = async (row, status) => {
    await api.put(`/bookings/${row.id}/status`, { status });
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status } : r)));
    toast.success("Booking updated");
  };

  return (
    <div data-testid="hq-bookings">
      {draft ? (
        <div className="mb-8 rounded-sm border border-border bg-[#141414] p-4">
          <SaveBar dirty={dirty} save={save} testId="catering" />
          <h3 className="display text-2xl uppercase">Catering settings & copy</h3>
          <p className="mono mt-1 text-[10px] leading-relaxed text-muted-foreground">
            EVERY NEW BOOKING REQUEST IS EMAILED TO THE NOTIFICATION ADDRESS BELOW — USE A REAL
            INBOX YOU CHECK. THE PUBLIC CONTACT EMAIL IS WHAT CUSTOMERS SEE ON THE SITE AND IN
            THE FOOTER; LEAVE THE PHONE BLANK TO HIDE IT.
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Field label="NOTIFICATION EMAIL">
              <Input data-testid="hq-catering-notify" value={draft.notify_email || ""}
                onChange={(e) => update({ ...draft, notify_email: e.target.value.trim() })}
                className={monoCls} />
            </Field>
            <Field label="PUBLIC CONTACT EMAIL">
              <Input data-testid="hq-catering-contact-email" value={draft.contact_email || ""}
                onChange={(e) => update({ ...draft, contact_email: e.target.value.trim() })}
                placeholder="info@smashlemonade.com" className={monoCls} />
            </Field>
            <Field label="PUBLIC CONTACT PHONE (OPTIONAL)">
              <Input data-testid="hq-catering-contact-phone" value={draft.contact_phone || ""}
                onChange={(e) => update({ ...draft, contact_phone: e.target.value })}
                placeholder="(801) 555-0134" className={monoCls} />
            </Field>
            <Field label="CONTACT BLOCK TITLE">
              <Input value={draft.contact_title || ""}
                onChange={(e) => update({ ...draft, contact_title: e.target.value })} className={inputCls} />
            </Field>
            <Field label="CONTACT BLOCK NOTE">
              <Input value={draft.contact_note || ""}
                onChange={(e) => update({ ...draft, contact_note: e.target.value })} className={inputCls} />
            </Field>
            <Field label="PAGE TITLE">
              <Input value={draft.title || ""}
                onChange={(e) => update({ ...draft, title: e.target.value })} className={inputCls} />
            </Field>
            <div className="md:col-span-2">
              <Field label="PAGE INTRO">
                <Textarea rows={3} value={draft.intro || ""}
                  onChange={(e) => update({ ...draft, intro: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <Field label="HOMEPAGE TEASER TITLE">
              <Input value={draft.teaser_title || ""}
                onChange={(e) => update({ ...draft, teaser_title: e.target.value })} className={inputCls} />
            </Field>
            <Field label="HOMEPAGE TEASER BUTTON">
              <Input value={draft.teaser_cta || ""}
                onChange={(e) => update({ ...draft, teaser_cta: e.target.value })} className={inputCls} />
            </Field>
            <div className="md:col-span-2">
              <Field label="HOMEPAGE TEASER BODY">
                <Textarea rows={2} value={draft.teaser_body || ""}
                  onChange={(e) => update({ ...draft, teaser_body: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="BULLETS (ONE PER LINE)">
                <Textarea rows={3} value={(draft.bullets || []).join("\n")}
                  onChange={(e) => update({ ...draft, bullets: e.target.value.split("\n") })}
                  className={inputCls} />
              </Field>
            </div>
            <Field label="EVENT TYPES (COMMA SEPARATED)">
              <Input data-testid="hq-catering-event-types" value={(draft.event_types || []).join(", ")}
                onChange={(e) => update({ ...draft, event_types: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                className={inputCls} />
            </Field>
            <Field label="SUCCESS MESSAGE">
              <Input value={draft.success || ""}
                onChange={(e) => update({ ...draft, success: e.target.value })} className={inputCls} />
            </Field>
            <div className="md:col-span-2">
              <Field label="DEPOSIT POLICY (SHOWN ON THE DEPOSIT EMAIL AND RECEIPT PAGE)">
                <Textarea data-testid="hq-deposit-policy" rows={2} value={draft.deposit_policy || ""}
                  onChange={(e) => update({ ...draft, deposit_policy: e.target.value })}
                  className={inputCls} />
              </Field>
            </div>
          </div>
        </div>
      ) : null}

      {error ? <div className="text-sm text-destructive">{error}</div> : null}
      {!rows ? (
        <div className="mono text-sm text-muted-foreground">Loading bookings…</div>
      ) : !rows.length ? (
        <div data-testid="hq-bookings-empty" className="mono text-sm text-muted-foreground">
          No event requests yet. They land here the moment someone books.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} data-testid={`hq-booking-${r.id}`} className="rounded-sm border border-border bg-[#141414] p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="display text-2xl uppercase leading-none">{r.name}</div>
                  <div className="mono mt-1 text-[10px] tracking-widest text-muted-foreground">
                    {r.email} · {r.phone || "no phone"} · {r.location || "no location"}
                  </div>
                  <div className="mono mt-2 text-[10px] tracking-widest text-primary">
                    {[r.event_type || "EVENT", r.event_date, r.start_time,
                      r.drink_count ? `${r.drink_count} DRINKS` : "",
                      r.package, r.service_option,
                      r.alani_count && r.alani_count !== "0" ? `${r.alani_count} ALANI` : "",
                    ].filter(Boolean).join(" · ")}
                  </div>
                  {r.flavors?.length ? (
                    <div data-testid={`hq-booking-flavors-${r.id}`} className="mono mt-2 text-[10px] tracking-widest text-muted-foreground">
                      FLAVORS: {r.flavors.join(" · ").toUpperCase()}
                    </div>
                  ) : null}
                  {r.notes ? <p className="mt-3 max-w-2xl text-sm text-white">{r.notes}</p> : null}
                </div>
                <div className="w-40">
                  <Pick testId={`hq-booking-status-${r.id}`} value={r.status}
                    onChange={(v) => setStatus(r, v)} options={BOOKING_STATUSES} />
                </div>
              </div>
              <div className="mono mt-3 text-[10px] tracking-widest text-muted-foreground">
                RECEIVED {(r.created_at || "").slice(0, 16).replace("T", " ")}
              </div>
              <DepositPanel
                booking={r}
                packages={content.cateringPackages}
                onPaid={(patch) =>
                  setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, ...patch } : x)))
                }
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const APP_STATUSES = ["NEW", "REVIEWING", "INTERVIEWING", "APPROVED", "DECLINED"];

export const ApplicationsInbox = () => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api
      .get("/applications")
      .then(({ data }) => setRows(data.applications))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message));
  }, []);

  const setStatus = async (row, status) => {
    await api.put(`/applications/${row.id}/status`, { status });
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, status } : r)));
    toast.success("Application updated");
  };

  if (error) return <div className="text-sm text-destructive">{error}</div>;
  if (!rows) return <div className="mono text-sm text-muted-foreground">Loading applications…</div>;
  if (!rows.length)
    return (
      <div data-testid="hq-applications-empty" className="mono text-sm text-muted-foreground">
        No operator applications yet. They land here the moment someone applies.
      </div>
    );

  return (
    <div className="space-y-3" data-testid="hq-applications">
      {rows.map((r) => (
        <div key={r.id} data-testid={`hq-application-${r.email}`} className="rounded-sm border border-border bg-[#141414] p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="display text-2xl uppercase leading-none">{r.name}</div>
              <div className="mono mt-1 text-[10px] tracking-widest text-muted-foreground">
                {r.email} · {r.phone || "no phone"} · {[r.city, r.state].filter(Boolean).join(", ") || "no location"}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="mono text-[10px] tracking-widest text-primary">
                {r.path || "PATH UNDECIDED"} · {r.start_timeline || "NO TIMELINE"}
              </span>
              <div className="w-40">
                <Pick testId={`hq-application-status-${r.email}`} value={r.status}
                  onChange={(v) => setStatus(r, v)} options={APP_STATUSES} />
              </div>
              <button
                data-testid={`hq-application-open-${r.email}`}
                onClick={() => setOpen(open === r.id ? null : r.id)}
                className="mono border border-border px-3 py-1.5 text-[10px] tracking-widest text-primary hover:border-primary"
              >
                {open === r.id ? "HIDE" : "READ"}
              </button>
            </div>
          </div>
          {open === r.id ? (
            <div className="mt-4 space-y-3 border-t border-border pt-4">
              {[
                ["Why a business", r.why_business],
                ["Doing currently", r.current_work],
                ["What it would change", r.what_would_change],
                ["Would operate it themselves", r.operate_yourself],
                ["Comfortable with events & customers", r.comfortable_with_events],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="mono text-[10px] tracking-widest text-muted-foreground">
                    {label.toUpperCase()}
                  </div>
                  <p className="mt-1 text-sm text-white">{value || "—"}</p>
                </div>
              ))}
              <div className="mono text-[10px] tracking-widest text-muted-foreground">
                RECEIVED {(r.created_at || "").slice(0, 16).replace("T", " ")}
              </div>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
};
